import type {
  PaymentRequest,
  PaymentRequestTokenEvent,
  StripeError,
  Token,
} from '@stripe/stripe-js';
import { useCallback, useRef } from 'react';

import { useWalletAvailability } from '@/context/walletAvailability';
import { Logger } from '@/utils/logging';

import { useStripe } from './useStripe';

const PAYMENT_SHEET_TIMEOUT_MS = 60_000;

type WalletCustomer = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
};

type WalletOptions = { currency?: string; country?: string };

type DigitalWalletPaymentResult = {
  token: Token | null;
  error: StripeError | null;
  cancelled: boolean;
  payload: {
    customer: WalletCustomer;
    paymentToken: string;
  } | null;
};

type SheetOutcome =
  | { kind: 'token'; event: PaymentRequestTokenEvent }
  | { kind: 'cancelled' }
  | { kind: 'error'; error: StripeError };

type PreparedRequest = {
  paymentRequest: PaymentRequest;
  currency: string;
  country: string;
};

const toRequestLocale = (options?: WalletOptions) => ({
  country: options?.country ?? 'US',
  currency: (options?.currency ?? 'usd').toLowerCase(),
});

const toPaymentResult = (
  outcome: SheetOutcome,
  customer: WalletCustomer,
): DigitalWalletPaymentResult => {
  if (outcome.kind === 'cancelled') {
    return { token: null, payload: null, error: null, cancelled: true };
  }
  if (outcome.kind === 'error') {
    return {
      token: null,
      payload: null,
      error: outcome.error,
      cancelled: false,
    };
  }

  const { event } = outcome;
  const name = (event.payerName ?? '').trim();
  const splitIdx = name.indexOf(' ');
  const firstName = splitIdx > 0 ? name.substring(0, splitIdx) : name;
  const lastName = splitIdx > 0 ? name.substring(splitIdx + 1) : '';

  return {
    token: event.token,
    payload: {
      paymentToken: event.token.id,
      customer: {
        firstName: firstName || customer.firstName,
        lastName: lastName || customer.lastName,
        email: event.payerEmail ?? customer.email,
        phone: event.payerPhone ?? customer.phone,
      },
    },
    error: null,
    cancelled: false,
  };
};

/**
 * Calls `show()` synchronously, so it must run inside the tap handler: the
 * browser rejects Apple Pay once an `await` has passed. Never rejects.
 */
const presentSheet = (
  paymentRequest: PaymentRequest,
  type: 'applePay' | 'googlePay',
) => {
  let settle: (outcome: SheetOutcome) => void = () => {};

  const outcome = new Promise<SheetOutcome>((resolve) => {
    const timeout = setTimeout(() => {
      Logger.error('Payment sheet timed out', { type });
      resolve({ kind: 'cancelled' });
    }, PAYMENT_SHEET_TIMEOUT_MS);

    settle = (result) => {
      clearTimeout(timeout);
      resolve(result);
    };

    paymentRequest.on('token', (event: PaymentRequestTokenEvent) => {
      settle({ kind: 'token', event });
      event.complete('success');
    });
    paymentRequest.on('cancel', () => settle({ kind: 'cancelled' }));

    try {
      paymentRequest.show();
    } catch (error) {
      Logger.warn('Payment sheet failed to open', {
        type,
        message: error instanceof Error ? error.message : String(error),
      });
      settle({ kind: 'error', error: { type: 'api_error' } });
    }
  });

  const abort = () => {
    try {
      paymentRequest.abort();
    } catch {
      // abort() throws when the sheet is not showing; nothing to close then.
    }
    settle({ kind: 'cancelled' });
  };

  return { outcome, abort };
};

export const useDigitalWalletPay = (type: 'applePay' | 'googlePay') => {
  const { googlePay, applePay } = useWalletAvailability();
  const isAvailable = type === 'googlePay' ? googlePay : applePay;
  const stripe = useStripe();
  const preparedRef = useRef<PreparedRequest | null>(null);

  /**
   * Builds the payment request and runs `canMakePayment()` ahead of the tap,
   * so `startDigitalWalletPayment` can show the sheet with no `await` first.
   */
  const prepareDigitalWalletPayment = useCallback(
    async (options: { label: string } & WalletOptions) => {
      if (!stripe) return;
      const locale = toRequestLocale(options);
      const paymentRequest = stripe.paymentRequest({
        ...locale,
        total: { label: options.label, amount: 0 },
        requestPayerName: true,
        requestPayerEmail: true,
        requestPayerPhone: true,
      });

      try {
        const canMakePayment = await paymentRequest.canMakePayment();
        preparedRef.current = canMakePayment
          ? { paymentRequest, ...locale }
          : null;
      } catch (err) {
        Logger.warn('canMakePayment threw while preparing payment', {
          type,
          err,
        });
        preparedRef.current = null;
      }
    },
    [stripe, type],
  );

  /**
   * Shows a prepared sheet right away. Returns null when nothing matching was
   * prepared; the caller then falls back to `makeDigitalWalletPayment`.
   */
  const startDigitalWalletPayment = (
    amount: number,
    label: string,
    options?: WalletOptions,
  ) => {
    const prepared = preparedRef.current;
    const locale = toRequestLocale(options);
    if (
      !prepared ||
      prepared.currency !== locale.currency ||
      prepared.country !== locale.country
    ) {
      return null;
    }

    // A request is consumed by one sheet; a second tap needs a new prepare.
    preparedRef.current = null;
    prepared.paymentRequest.update({ total: { label, amount } });
    const { outcome, abort } = presentSheet(prepared.paymentRequest, type);

    return {
      result: async (customer: WalletCustomer) =>
        toPaymentResult(await outcome, customer),
      abort,
    };
  };

  const makeDigitalWalletPayment = async (
    amount: number,
    label: string,
    customer: WalletCustomer,
    options?: WalletOptions,
  ): Promise<DigitalWalletPaymentResult> => {
    if (!stripe) {
      Logger.error('Stripe not loaded for digital wallet payment', { type });
      return {
        token: null,
        payload: null,
        error: {
          type: 'validation_error',
          message: 'Stripe has not loaded yet.',
        },
        cancelled: false,
      };
    }

    const paymentRequest = stripe.paymentRequest({
      ...toRequestLocale(options),
      total: { label, amount },
      requestPayerName: true,
      requestPayerEmail: true,
      requestPayerPhone: true,
    });

    let canMakePayment;
    try {
      canMakePayment = await paymentRequest.canMakePayment();
    } catch (err) {
      Logger.error('canMakePayment threw during payment setup', { type, err });
      return {
        token: null,
        payload: null,
        cancelled: false,
        error: {
          type: 'api_error',
          message: 'Unable to check payment availability.',
        },
      };
    }

    if (!canMakePayment) {
      return {
        token: null,
        payload: null,
        error: {
          type: 'validation_error',
          message: 'This payment method is not available.',
        },
        cancelled: false,
      };
    }

    const { outcome } = presentSheet(paymentRequest, type);
    return toPaymentResult(await outcome, customer);
  };

  return {
    isAvailable,
    isLoading: false,
    makeDigitalWalletPayment,
    prepareDigitalWalletPayment,
    startDigitalWalletPayment,
  };
};
